import { create } from 'zustand';
import type { BillingType, SubscriptionData, SubscriptionStatus, SubscriptionTier } from '../types/platform.types';

interface SubscriptionState {
  enabled_modules: string[];
  tier: SubscriptionTier | null;
  status: SubscriptionStatus | null;
  billing_type: BillingType | null;
  trial_ends_at?: string;
  current_period_end: string | null;
  seat_count: number;
  max_seats: number;
  ai_copilot_free_calls_remaining: number;
  ai_copilot_monthly_quota: number;
  enabled_add_ons: string[];
  bootstrapComplete: boolean;

  setSubscription: (data: SubscriptionData) => void;
  setBootstrapComplete: () => void;
  resetBootstrap: () => void;
  reset: () => void;
  hasModule: (moduleId: string) => boolean;
  isLocked: () => boolean;
  isTrial: () => boolean;
  isManual: () => boolean;
  setCopilotCallsRemaining: (n: number | null) => void;
  hasCopilotAddon: () => boolean;
}

export const useSubscriptionStore = create<SubscriptionState>()((set, get) => ({
  enabled_modules: [],
  tier: null,
  status: null,
  billing_type: null,
  trial_ends_at: undefined,
  current_period_end: null,
  seat_count: 0,
  max_seats: 0,
  ai_copilot_free_calls_remaining: 25,
  ai_copilot_monthly_quota: 25,
  enabled_add_ons: [],
  bootstrapComplete: false,

  setSubscription: (data) =>
    set({
      enabled_modules:                 data.enabled_modules,
      tier:                            data.tier,
      status:                          data.status,
      billing_type:                    data.billing_type ?? null,
      trial_ends_at:                   data.trial_ends_at,
      current_period_end:              data.current_period_end,
      seat_count:                      data.seat_count,
      max_seats:                       data.max_seats,
      ai_copilot_free_calls_remaining: data.ai_copilot_free_calls_remaining ?? 25,
      ai_copilot_monthly_quota:        data.ai_copilot_monthly_quota ?? 25,
      enabled_add_ons:                 data.enabled_add_ons ?? [],
    }),

  setBootstrapComplete: () => set({ bootstrapComplete: true }),

  resetBootstrap: () => set({ bootstrapComplete: false }),

  reset: () =>
    set({
      enabled_modules: [],
      tier: null,
      status: null,
      billing_type: null,
      trial_ends_at: undefined,
      current_period_end: null,
      seat_count: 0,
      max_seats: 0,
      ai_copilot_free_calls_remaining: 25,
      ai_copilot_monthly_quota: 25,
      enabled_add_ons: [],
      bootstrapComplete: false,
    }),

  hasModule: (moduleId) => get().tier !== null && get().enabled_modules.includes(moduleId),

  isLocked: () => get().status === 'locked',

  isTrial: () => get().status === 'trial',

  isManual: () => get().billing_type === 'manual',

  setCopilotCallsRemaining: (n) =>
    set({ ai_copilot_free_calls_remaining: n ?? 25 }),

  // Returns true if company has the "AI Copilot Unlimited" add-on.
  // Checks enabled_add_ons first; falls back to -1 sentinel (unlimited) from the quota field.
  hasCopilotAddon: () =>
    get().enabled_add_ons.includes('ai_copilot') ||
    get().ai_copilot_free_calls_remaining === -1,
}));
